import { getRequestCredentials, request } from "@prismicio/editor-support/Http"

import {
  type EditorConfig,
  useEditorConfig,
} from "../../../../../../EditorConfig"
import {
  type MediaAsset,
  mediaAssetType,
} from "../../../../hooks/mediaLibraryData"
import { type Tag, updateTagCache } from "../../../../hooks/tagData"
import { useMediaLibrary } from "../../../MediaLibraryContext"
import { type addOrRemove, Tags } from "./Tags"

interface SingleMediaMetadataTagsProps {
  selectedMedia: MediaAsset
}

export function SingleMediaMetadataTags(props: SingleMediaMetadataTagsProps) {
  const { selectedMedia } = props
  const tags = selectedMedia.tags

  const config = useEditorConfig()
  const { updateMedia } = useMediaLibrary()

  async function toggleAssetTag(tag: Tag, action: addOrRemove) {
    const oldMediaTags = selectedMedia.tags
    const isRemove = action === "remove"
    const newMediaTags = isRemove
      ? oldMediaTags.filter((mediaTag) => mediaTag.id !== tag.id)
      : [...oldMediaTags, tag]

    updateMedia(selectedMedia.id, { tags: newMediaTags })

    try {
      await updateAssetTags({
        mediaId: selectedMedia.id,
        config,
        tags: newMediaTags.map((tag) => tag.id),
      })

      updateTagCache((tags) =>
        tags.map((cachedTag) =>
          cachedTag.id === tag.id
            ? { ...cachedTag, count: cachedTag.count + (isRemove ? -1 : 1) }
            : cachedTag,
        ),
      )
    } catch (e) {
      updateMedia(selectedMedia.id, { tags: oldMediaTags })

      throw e
    }
  }
  function getTagsLabel() {
    const numberOfTags = tags.length
    if (numberOfTags > 1) {
      return `${numberOfTags} tags`
    }
    if (numberOfTags === 1) {
      return "1 tag"
    }
    return "Tags"
  }

  return (
    <Tags
      tags={tags}
      getTagsLabel={getTagsLabel}
      toggleAssetTag={toggleAssetTag}
    />
  )
}

interface UpdateAssetTagsArgs {
  mediaId: string
  config: EditorConfig
  tags: string[]
}

function updateAssetTags(params: UpdateAssetTagsArgs) {
  const { mediaId, config, tags } = params

  if (!config.mediaLibrary) throw new Error("Media Library not configured.")

  const { assetsApiBaseUrl, repository, authStrategy } = config

  return request(new URL(`assets/${mediaId}`, assetsApiBaseUrl), {
    headers: { repository },
    body: { tags },
    method: "PATCH",
    credentials: getRequestCredentials(authStrategy),
    okType: mediaAssetType,
  })
}
